/* Trial / pending / rejected / expired banner above tenant pages (prototype subBanner).
 * Owners get the full banner from GET /billing; HR and accountants (who may not call billing
 * endpoints) get a light trial notice derived from their notifications feed. */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/session";
import { useBilling, useNotifications } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import { Icon, cx } from "../../ui";

function Bar({ tone, icon, children, link, testId }: { tone?: string; icon: string; children: ReactNode; link?: [string, string]; testId: string }) {
  return (
    <div className={cx("subbar", tone)} data-testid={testId} role="status">
      <Icon name={icon} className="s" /><span>{children}</span>
      {link && <Link to={link[0]} className="subbar-a">{link[1]}</Link>}
    </div>
  );
}

function OwnerBanner() {
  const { L, lang } = useI18n();
  const q = useBilling();
  const v = q.data;
  if (!v) return null;
  const s = v.subscription;
  if (s.status === "active") return null;
  const p = v.payments[0];
  if (p?.status === "Pending") return <Bar tone="info" icon="clock" testId="subbar-pending" link={["/billing", L("View status", "عرض الحالة")]}>{L("Your bank transfer receipt is being verified. This usually takes less than one business day.", "جارٍ التحقق من إيصال التحويل البنكي، ويستغرق عادة أقل من يوم عمل.")}</Bar>;
  if (p?.status === "Rejected") return <Bar tone="bad" icon="alert" testId="subbar-rejected" link={["/billing", L("Upload again", "إعادة الرفع")]}>{L("Payment receipt rejected: ", "تم رفض الإيصال: ")}{p.reason}</Bar>;
  const left = v.trialLeft;
  if (left <= 0 || s.status === "expired") return <Bar tone="warn" icon="alert" testId="subbar-expired" link={["/billing", L("Choose a plan", "اختر باقة")]}>{L("Your free trial has ended. Subscribe to keep running payroll.", "انتهت التجربة المجانية. اشترك لمواصلة تشغيل الرواتب.")}</Bar>;
  return (
    <Bar tone={left <= 5 ? "warn" : undefined} icon="sparkle" testId="subbar-trial" link={["/billing", L("Choose a plan", "اختر باقة")]}>
      {L("Free trial · ", "التجربة المجانية · ")}<b>{L(`${left} days left`, `متبقٍ ${left} يومًا`)}</b>{L(" · ends ", " · تنتهي ")}{gDate(v.trialEnd, lang)}
    </Bar>
  );
}

function StaffNotice() {
  const { L } = useI18n();
  const q = useNotifications();
  const n = (q.data || []).find((x) => x.id === "n-trial");
  if (!n || n.tone === "ok") return null;
  const m = /(\d+)\s+(?:trial\s+)?days/.exec(n.body);
  const left = m ? Number(m[1]) : null;
  const owner = L(" · your company owner manages billing", " · يدير مالك المنشأة الفوترة");
  if (/review/i.test(n.title)) return <Bar tone="info" icon="clock" testId="subbar-staff">{L("Subscription payment is being verified", "جارٍ التحقق من دفعة الاشتراك")}{owner}</Bar>;
  if (left === 0) return <Bar tone="warn" icon="alert" testId="subbar-staff">{L("The free trial has ended", "انتهت التجربة المجانية")}{owner}</Bar>;
  return (
    <Bar tone={left != null && left <= 5 ? "warn" : undefined} icon="sparkle" testId="subbar-staff">
      {L("Free trial · ", "التجربة المجانية · ")}{left != null ? <b>{L(`${left} days left`, `متبقٍ ${left} يومًا`)}</b> : n.body}{owner}
    </Bar>
  );
}

export default function SubBanner() {
  const { role } = useSession();
  if (role === "owner") return <OwnerBanner />;
  if (role === "hr" || role === "accountant") return <StaffNotice />;
  return null;
}
